import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";

import { API_BASE_URL } from "../../config";
import { useNavigate, Link } from "react-router-dom"; // Added Link
import "./FeaturedBusinessBanner.css";

/**
 * Star SVG with support for empty, half, and filled variants without external icon libs.
 */
const Star = ({ variant = "empty", id, size = 16 }) => {
  const starPath =
    "M12 .587l3.668 7.568L24 9.423l-6 5.852 1.417 8.261L12 18.897 4.583 23.536 6 14.275 0 9.423l8.332-1.268L12 .587z";
  const halfClipId = `fbb-clip-${id}`;

  if (variant === "half") {
    return (
      <svg
        viewBox="0 0 24 24"
        width={size}
        height={size}
        className="fbb-star"
        aria-hidden="true"
      >
        <defs>
          <clipPath id={halfClipId}>
            <rect x="0" y="0" width="12" height="24" />
          </clipPath>
        </defs>
        {/* Base empty star */}
        <path d={starPath} className="fbb-star-empty" />
        {/* Half-filled star clipped to left half */}
        <path d={starPath} className="fbb-star-filled" clipPath={`url(#${halfClipId})`} />
      </svg>
    );
  }

  return (
    <svg
      viewBox="0 0 24 24"
      width={size}
      height={size}
      className="fbb-star"
      aria-hidden="true"
    >
      <path d={starPath} className={variant === "filled" ? "fbb-star-filled" : "fbb-star-empty"} />
    </svg>
  );
};

const StarRating = ({ rating = 0 }) => {
  const uidRef = useRef(Math.random().toString(36).slice(2));
  const full = Math.floor(Number(rating) || 0);
  const hasHalf = (Number(rating) || 0) - full >= 0.5;

  return (
    <div className="fbb-stars">
      {Array.from({ length: 5 }).map((_, i) => {
        const idx = i + 1;
        let variant = "empty";
        if (idx <= full) variant = "filled";
        else if (idx === full + 1 && hasHalf) variant = "half";

        return <Star key={`${uidRef.current}-${idx}`} id={`${uidRef.current}-${idx}`} variant={variant} size={14} />;
      })}
    </div>
  );
};

/**
 * Normalizes backend business model into a uniform shape for the banner.
 */
function toAbsoluteImageUrl(url) {
  if (!url || typeof url !== "string") return "/img/img-logo.png";
  const trimmed = url.trim();

  // Already a data/blob URL
  if (trimmed.startsWith("data:") || trimmed.startsWith("blob:")) return trimmed;

  // Heuristic: raw base64 (no scheme, long, base64 charset)
  const maybeBase64 = /^[A-Za-z0-9+/=]+$/.test(trimmed) && trimmed.length > 100 && !/^https?:\/\//i.test(trimmed);
  if (maybeBase64) return `data:image/jpeg;base64,${trimmed}`;

  // Absolute URL
  try {
    const test = new URL(trimmed);
    return test.href;
  } catch (e) {
    // not absolute
  }

  // API-relative path
  if (trimmed.startsWith("/")) {
    return `${API_BASE_URL}${trimmed}`;
  }

  return trimmed;
}

const normalizeBusiness = (b) => {
  const id =
    b.businessUserId ??
    b.businessId ??
    b.id ??
    b.BusinessUserId ??
    b.BusinessId ??
    null;

  const name = b.businessName ?? b.name ?? b.BusinessName ?? "Business";
  const postcode = b.postCode ?? b.postcode ?? b.PostCode ?? "";
  const rawImage =
    b.profileImage ??
    b.profilePic ??
    b.profile_Pic ??
    b.image ??
    b.imageUrl ??
    null;
  const image = toAbsoluteImageUrl(rawImage);
  const rating =
    b.overallRating !== null && b.overallRating !== undefined
      ? parseFloat(b.overallRating)
      : 0;
  const reviewCount =
    b.reviewCount ?? b.ratingCount ?? b.RatingCount ?? b.ReviewCount ?? null;
  const suburb = b.suburb ?? b.Suburb ?? "";
  return { id, name, postcode, image, rating, reviewCount, suburb };
};


const FeaturedBusinessBanner = () => {
  const [businesses, setBusinesses] = useState([]);
  const [paused, setPaused] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let isMounted = true;
    const fetchFeatured = async () => {
      try {
        const response = await axios.get(`${API_BASE_URL}/api/Business/getFeaturedBusinesses`);
        if (!isMounted) return;
        const list = Array.isArray(response.data) ? response.data : [];
        const normalized = list.map(normalizeBusiness).filter((x) => x.id !== null);
        setBusinesses(normalized);
      } catch (e) {
        setBusinesses([]);
      }
    };
    fetchFeatured();
    return () => { isMounted = false; };
  }, []);

  const trackItems = useMemo(() => {
    if (!businesses || businesses.length === 0) return [];
    return [...businesses, ...businesses];
  }, [businesses]);
  
  const handleCardClick = (e, id) => {
    e.preventDefault();
    if (id != null) navigate(`/business/${id}`);
  };

  // --- RENDER ---
  if (!businesses || businesses.length === 0) {
    return (
      <section className="fbb-section">
        <div className="fbb-shine" aria-hidden="true" />
        <div className="fbb-container fbb-empty">
           <div className="fbb-empty-text">Loading Featured Businesses...</div>
        </div>
      </section>
    );
  }

  return (
    <section className="fbb-section">
      <div className="fbb-shine" aria-hidden="true" />

      {/* --- NEW HEADER: INTEGRATED LINK --- */}
      <Link to="/sponsors" className="fbb-header-link" title="View all Sponsors">
        <span className="fbb-header-text">
          Our Sponsors and Partners
          {/* Arrow SVG Icon */}
          <span className="fbb-header-icon">
             <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                <path d="M5 12h14M12 5l7 7-7 7"/>
             </svg>
          </span>
        </span>
      </Link>

      <div
        className="fbb-container"
        onMouseEnter={() => setPaused(true)}
        onMouseLeave={() => setPaused(false)}
        onTouchStart={() => setPaused(true)}
        onTouchEnd={() => setPaused(false)}
      >
        <div className={`fbb-track ${paused ? "paused" : ""}`}>
          {trackItems.map((biz, idx) => {
            const addr = biz.suburb || biz.postcode ? `${biz.suburb || ""}${biz.suburb && biz.postcode ? ", " : ""}${biz.postcode || ""}` : "";
            return (
              <a
                key={`${biz.id ?? "x"}-${idx}`}
                href={`/business/${biz.id ?? ""}`}
                className="fbb-card"
                onClick={(e) => handleCardClick(e, biz.id)}
                aria-label={biz.name}
                title={biz.name}
              >
                <img 
                  src={biz.image} 
                  alt={biz.name} 
                  className="fbb-image" 
                  decoding="async" 
                  loading="lazy" 
                  draggable="false"
                  sizes="(max-width: 768px) 200px, 260px"
                />
                <div className="fbb-overlay" />
                <div className="fbb-details">
                  <h3 className="fbb-name">{biz.name}</h3>
                  {addr ? <p className="fbb-profession">Top Rated in {addr}</p> : <p className="fbb-profession">Featured Business</p>}
                  <div className="fbb-rating">
                    <StarRating rating={biz.rating} />
                    <span className="fbb-rating-text">
                      {biz.rating ? biz.rating.toFixed(1) : "New"}
                    </span>
                  </div>
                </div>
              </a>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default FeaturedBusinessBanner;